import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { errorMessage } from "../api/client";
import { useRegisterPayment } from "../api/payments";
import type { PaymentUserRow } from "../api/types";
import {
  formatBalance,
  localInputToIso,
  nowLocalInputValue,
  userLabel,
} from "../lib/payments";

interface Props {
  user: PaymentUserRow | null; // null = closed
  onClose: () => void;
  onSaved: (message: string) => void;
}

/**
 * Register whole hours paid to one user. Minutes are never paid: the input only takes an
 * integer, capped at the user's payable hours (the server re-checks and answers 409 if the
 * balance moved under us — that message is shown verbatim).
 */
export default function RegisterPaymentDialog({ user, onClose, onSaved }: Props) {
  const [hours, setHours] = useState("");
  const [paidAt, setPaidAt] = useState(nowLocalInputValue);
  const [note, setNote] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const hoursRef = useRef<HTMLInputElement>(null);
  const register = useRegisterPayment();

  // reset the form every time a (different) user is opened
  useEffect(() => {
    if (!user) return;
    setHours("");
    setPaidAt(nowLocalInputValue());
    setNote("");
    setLocalError(null);
    register.reset();
    hoursRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  if (!user) return null;

  const payable = user.payable_hours;
  const parsed = hours.trim() === "" ? NaN : Number(hours);
  const leftoverMs = user.pending_ms - payable * 3_600_000;
  const previewAfter = Number.isInteger(parsed) ? user.pending_ms - parsed * 3_600_000 : null;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!Number.isInteger(parsed) || parsed < 1) {
      setLocalError("Enter a whole number of hours (1 or more).");
      return;
    }
    if (parsed > payable) {
      setLocalError(`Cannot exceed the ${payable}h payable now.`);
      return;
    }
    if (!paidAt) {
      setLocalError("Choose when the payment was made.");
      return;
    }
    setLocalError(null);
    register.mutate(
      {
        user_uid: user!.uid,
        hours: parsed,
        paid_at: localInputToIso(paidAt),
        note: note.trim() || null,
      },
      {
        onSuccess: () => {
          onSaved(`Payment of ${parsed}h registered for ${userLabel(user!)}`);
          onClose();
        },
      },
    );
  }

  const error = localError ?? (register.isError ? errorMessage(register.error) : null);

  return createPortal(
    <div
      className="modal-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <form className="modal pay-modal" role="dialog" aria-modal="true" onSubmit={submit}>
        <h3 className="modal-title">Register payment</h3>
        <p className="muted small pay-modal-who">
          {userLabel(user)} · pending {formatBalance(user.pending_ms)}
        </p>

        <label className="field">
          <span className="field-label">Hours paid</span>
          <input
            ref={hoursRef}
            className="text-input"
            type="number"
            inputMode="numeric"
            min={1}
            max={payable}
            step={1}
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            placeholder="0"
          />
          <span className="small muted">
            Whole hours only. Payable now: <b>{payable}h</b>
            {leftoverMs > 0 && <> (the remaining {formatBalance(leftoverMs)} carries over)</>}.
          </span>
        </label>

        <div className="quick-fill" aria-label="Quick fill">
          <button type="button" className="btn small" onClick={() => setHours(String(payable))}>
            All payable ({payable}h)
          </button>
          {[5, 10, 20]
            .filter((q) => q < payable)
            .map((q) => (
              <button key={q} type="button" className="btn small" onClick={() => setHours(String(q))}>
                {q}h
              </button>
            ))}
        </div>

        <label className="field">
          <span className="field-label">Paid on</span>
          <input
            className="text-input"
            type="datetime-local"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
            required
          />
        </label>

        <label className="field">
          <span className="field-label">
            Note <span className="faint">(optional)</span>
          </span>
          <input
            className="text-input"
            value={note}
            maxLength={500}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. August batch, PIX"
          />
        </label>

        {previewAfter != null && (
          <p className="small muted pay-modal-preview">
            Pending after this payment:{" "}
            <b className={previewAfter < 0 ? "error" : ""}>
              {previewAfter < 0 ? "exceeds balance" : formatBalance(previewAfter)}
            </b>
          </p>
        )}
        <p className="error small pay-modal-error" aria-live="polite">
          {error ?? " "}
        </p>

        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose} disabled={register.isPending}>
            Cancel
          </button>
          <button type="submit" className="btn primary" disabled={register.isPending}>
            {register.isPending ? "Saving…" : "Save payment"}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
